<template>
  <div class="progress-bar my-5">
    <div class="flex justify-between w-5/6 sm:w-4/6 mx-auto mb-1 sm:mb-4">
      <div v-for="tier in loyaltyTiers" :key="tier.id">
        <div class="w-full">
          <template v-if="isTierReached(tier)">
            <img v-if="tier.reachedIconUrl" :alt="`${tier.name[$i18n.locale]}-reached`" :src="tier.reachedIconUrl" />
          </template>
          <template v-else>
            <img v-if="tier.lockedIconUrl" :alt="`${tier.name[$i18n.locale]}-locked`" :src="tier.lockedIconUrl" />
          </template>
        </div>
      </div>
    </div>

    <div class="progress-bar-line relative w-5/6 sm:w-4/6 mx-auto mb-1">
      <div class="bar z-0 absolute rounded-full" :style="{ backgroundColor: '#A3A3A3' }">
        <div
          :class="['bar-inner text-xs font-medium text-black text-right leading-none rounded-full', getActiveStep()]"
          :style="{ backgroundColor: '#000000' }"
        >
        </div>
      </div>

      <div class="circles flex justify-between">
        <div v-for="tier in loyaltyTiers"
          :key="tier.id"
          :class="['flex items-center rounded-full']"
          :style="{ backgroundColor: isTierReached(tier) ? '#000000' : '#A3A3A3' }"
         >
          &nbsp;
        </div>
      </div>
    </div>

    <div class="circles-description flex justify-between mx-auto">
      <div v-for="(tier, index) in loyaltyTiers" :key="tier.id" :class="['w-1/3 px-4', {'pl-0 sm:pl-16': index === 0, 'pr-0 sm:pr-16': index === loyaltyTiers.length - 1}]">
        <p class="name text-center text-xs sm:text-sm uppercase font-bold">{{tier.name[$i18n.locale]}}-Status</p>
        <p class="description text-center hidden sm:block">{{tier.description[$i18n.locale]}}</p>
        <p class="short-description text-center text-xs block sm:hidden">{{tier.shortDescription[$i18n.locale]}}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, PropType } from "vue";
import { LoyaltyUser } from "@/models/loyaltyUser";
import { LoyaltyTier } from "@/models/loyaltyTier";

const {t} = useI18n();

const props = defineProps({
  loyaltyUser: {
    type: Object as PropType<LoyaltyUser>,
    required: true
  },
  loyaltyTiers: {
    type: Array as PropType<LoyaltyTier[]>,
    required: true
  }
})

const reversedTiers = [...props.loyaltyTiers].reverse();

const currentTier = computed<LoyaltyTier|any>(() => {
  return reversedTiers.find(tier => props.loyaltyUser.runtime >= tier.runtimeToReach);
});

function isTierReached(tier: LoyaltyTier) {
  return props.loyaltyUser.runtime >= tier.runtimeToReach;
}

function getActiveStep() {
  for (const [index, tier] of props.loyaltyTiers.entries()) {
    if (props.loyaltyUser.runtime == tier.runtimeToReach) {
      return `step-${index}`;
    } else if (tier.id == currentTier.value.id) {
      return `step-${index}-5`;
    }
  }
}
</script>

<style>
  .progress-bar .progress-bar-line {
    padding: 0 22px;
  }

  .progress-bar .bar {
    width: calc(100% - (22px * 2));
    margin-top: 8px;
  }

  .progress-bar .bar-inner {
    height: 4px;
  }

  .progress-bar .circles .rounded-full {
    width: 20px;
    height: 20px;
  }

  .step-0 {
    width: 20px;
  }
  .step-0-5 {
    width: 25%;
  }
  .step-1 {
    width: calc(50% + 10px);
  }
  .step-1-5 {
    width: calc(75% - 10px);
  }
  .step-2,
  .step-2-5 {
    width: 100%;
  }

  .circles-description .description {
    font-size: 12px;
    line-height: 1.4;
  }
</style>
